import './styles.sass';

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import ResearchOutput from 'components/Explorer/models/research_output';
import * as QueryParams from 'components/Explorer/models/query_params';
import useQueryParams from 'hooks/useQueryParams';
import useNavigateParams from 'hooks/useNavigateParams';
import Spinner from 'components/Explorer/Spinner';
import { XCircleIcon } from '@phosphor-icons/react';

const buildIframeUrl = (outputId) => {
  const configSubdomain = window.site_config.detailsPageSubdomain;
  const configDomain = window.site_config.altmetricDomain;
  const path = `/details/${outputId}`;

  // EZProxy and similar institutional proxies need the iframe to share the
  // Explorer origin so they can intercept it; otherwise they can't rewrite
  // the URL to route through their proxy.
  const useProxyUrl = configSubdomain === 'explorer' && configDomain === 'altmetric.com';
  if (useProxyUrl) {
    return `https://explorer.altmetric.com${path}`;
  }
  return `https://${configSubdomain}.${configDomain}${path}`;
};

const DetailPage = ({ output }) => {
  const params = useQueryParams();
  const navigateParams = useNavigateParams();

  const containerRef = useRef(null);
  const closeButtonRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const externalElementsRef = useRef([]);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    previouslyFocusedRef.current = document.activeElement;
    externalElementsRef.current = Array.from(document.querySelectorAll('[data-inertable]'));
    externalElementsRef.current.forEach((element) => (element.inert = true));

    const closeOnEscape = (event) => {
      if (event.key === 'Escape' || event.data?.key === 'Escape') {
        closeButtonRef.current?.click();
      }
    };
    window.addEventListener('keydown', closeOnEscape);
    window.addEventListener('message', closeOnEscape);
    containerRef.current?.focus();

    return () => {
      window.removeEventListener('keydown', closeOnEscape);
      window.removeEventListener('message', closeOnEscape);
    };
  }, []);

  const onClose = () => {
    externalElementsRef.current.forEach((element) => (element.inert = false));

    Analytics.trackEvent('close-detail-page', { outputId: output.id });

    navigateParams(QueryParams.withShowDetails(params, null));

    requestAnimationFrame(() => {
      previouslyFocusedRef.current?.focus();
    });
  };

  return (
    <div>
      <button
        type="button"
        className="DetailPage-overlay"
        onClick={onClose}
        aria-label="Close details page"
        tabIndex={-1}
      />
      <div
        className="DetailPage"
        ref={containerRef}
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        aria-labelledby="detailpage-title"
      >
        <div className="DetailPage-header DetailPage-title">
          <div className="content" id="detailpage-title">
            {I18n.t('Explorer.DetailPage.title')}
          </div>

          <div className="buttons">
            <button ref={closeButtonRef} className="close" onClick={onClose}>
              <XCircleIcon size="24px" />
            </button>
          </div>
        </div>
        <div className="DetailPage-content">
          {loading && <Spinner />}
          {/* eslint-disable-next-line jsx-a11y/iframe-has-title -- using aria-labelledby instead of title to avoid browser hover tooltip */}
          <iframe
            src={buildIframeUrl(output.id)}
            className={loading ? 'not-visible' : 'visible'}
            onLoad={() => setLoading(false)}
            allow="clipboard-write"
            aria-labelledby="detailpage-title"
          />
        </div>
      </div>
    </div>
  );
};

DetailPage.propTypes = {
  output: PropTypes.instanceOf(ResearchOutput).isRequired
};

export default DetailPage;
